import React, { useEffect, useState } from "react";
import { API_BASE, authHeaders } from "../../utils/api";
import "./LatestNews.css";

const LatestNews = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    category: "",
    news_title: "",
    description: "",
    news_date: "",
    images: [],
    link_text: "",
    link_url: "",
    display_order: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUserRole(payload.role || "");
      } catch (e) {
        console.error("Error parsing token", e);
      }
    }
    fetchLatestNews();
  }, [statusFilter]);

  const fetchLatestNews = async () => {
    setLoading(true);
    setError("");
    try {
      const url =
        statusFilter === "all"
          ? `${API_BASE}/api/admin/latest-news`
          : `${API_BASE}/api/admin/latest-news?status=${statusFilter}`;
      const res = await fetch(url, { headers: authHeaders() });
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(
          `Server error: ${res.status} ${res.statusText}. ${text.substring(0, 80)}`,
        );
      }
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to load latest news");
      setLatestNews(data || []);
    } catch (err) {
      setError(err.message || "Failed to load latest news");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const previews = [];

    for (const file of files) {
      if (file.size > 500 * 1024) {
        setError("Each image must be less than 500 KB");
        continue;
      }
      validFiles.push(file);
      const reader = new FileReader();
      reader.onloadend = () => previews.push(reader.result);
      reader.readAsDataURL(file);
    }

    setForm((prev) => ({ ...prev, images: validFiles }));
    setTimeout(() => setPreviewImages(previews), 100);
  };

  const removeImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setForm({
      category: "",
      news_title: "",
      description: "",
      news_date: "",
      images: [],
      link_text: "",
      link_url: "",
      display_order: 0,
    });
    setEditingId(null);
    setPreviewImages([]);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.category) {
      setError("Category is required");
      return;
    }

    const formData = new FormData();
    formData.append("category", form.category);
    formData.append("news_title", form.news_title);
    formData.append("description", form.description || "");
    if (form.news_date) {
      formData.append("news_date", form.news_date);
    }
    formData.append("link_text", form.link_text || "");
    formData.append("link_url", form.link_url || "");
    formData.append("display_order", form.display_order || 0);
    form.images.forEach((file) => formData.append("news_photo", file));

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/latest-news/${editingId}`
        : `${API_BASE}/api/admin/latest-news`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to save latest news");

      setSuccess(data.message || "Saved");
      resetForm();
      window.location.reload();
    } catch (err) {
      setError(err.message || "Failed to save latest news");
    }
  };

  const handleEdit = (item) => {
    setForm({
      category: item.category || "",
      news_title: item.news_title || "",
      description: item.description || "",
      news_date: item.news_date || "",
      images: [],
      link_text: item.link_text || "",
      link_url: item.link_url || "",
      display_order: item.display_order ?? 0,
    });
    setEditingId(item.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/latest-news/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete");
      setSuccess("Deleted");
      fetchLatestNews();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id) => {
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/latest-news/${id}/approve`,
        {
          method: "POST",
          headers: authHeaders(),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to approve");
      setSuccess("Approved");
      fetchLatestNews();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Reject this entry?")) return;
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/latest-news/${id}/reject`,
        {
          method: "POST",
          headers: authHeaders(),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to reject");
      setSuccess("Rejected");
      fetchLatestNews();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="latest-news-admin-page">
      <div className="page-heading">
        <h1>Latest News</h1>
        <p>Manage latest news articles and updates.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="latest-news-grid">
        <div className="card">
          <h2>{editingId ? "Edit Latest News" : "Add Latest News"}</h2>
          <form className="latest-news-form" onSubmit={handleSubmit}>
            <label>
              Category *
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                <option value="admissions">Admissions</option>
                <option value="happenings">Happenings</option>
              </select>
            </label>
            <label>
              Title
              <input
                name="news_title"
                value={form.news_title}
                onChange={handleChange}
              />
            </label>
            <label>
              Description
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows="3"
              />
            </label>
            <label>
              Date
              <input
                type="text"
                name="news_date"
                value={form.news_date}
                onChange={handleChange}
                placeholder="e.g., 03 May 2024"
              />
            </label>
            <label>
              Link Text
              <input
                name="link_text"
                value={form.link_text}
                onChange={handleChange}
              />
            </label>
            <label>
              Link URL
              <input
                name="link_url"
                value={form.link_url}
                onChange={handleChange}
              />
            </label>
            <label>
              Display Order
              <input
                type="number"
                name="display_order"
                value={form.display_order}
                onChange={handleChange}
              />
            </label>
            <label>
              Images (Max 500 KB each)
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              {previewImages.length > 0 && (
                <div className="image-preview-item">
                  {previewImages.map((preview, index) => (
                    <div key={index} style={{ position: "relative" }}>
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => removeImage(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Add"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="card latest-news-list-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h2>Latest News Entries</h2>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #ddd",
                fontSize: "14px",
              }}
            >
              <option value="all">All Entries</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : latestNews.length === 0 ? (
            <p>No entries yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {latestNews.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {Array.isArray(item.news_photo) &&
                      item.news_photo.length > 0 ? (
                        <img
                          src={`${API_BASE}${item.news_photo[0]}`}
                          alt={item.news_title}
                          className="news-thumb"
                        />
                      ) : (
                        <div className="news-thumb-placeholder">No Image</div>
                      )}
                    </td>
                    <td>{item.category}</td>
                    <td>{item.news_title}</td>
                    <td>{item.news_date}</td>
                    <td>
                      <span className={`status status-${item.status}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(item)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(item.id)}
                      >
                        🗑️ Delete
                      </button>
                      {item.status !== "approved" &&
                        userRole === "superadmin" && (
                          <button
                            className="btn-edit"
                            onClick={() => handleApprove(item.id)}
                          >
                            ✅ Approve
                          </button>
                        )}
                      {item.status !== "rejected" &&
                        item.status !== "approved" &&
                        userRole === "superadmin" && (
                          <button
                            className="btn-delete"
                            onClick={() => handleReject(item.id)}
                          >
                            ❌ Reject
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LatestNews;
